/**
 * Binding Utilities for Visual Template Editor
 *
 * Handles {{content.xxx}} data binding resolution, extraction,
 * and JSON path manipulation for inline editing.
 */

/**
 * Resolve a dot-separated path in a data object
 * e.g. resolveDataPath("brand.name", { brand: { name: "MyBrand" } }) → "MyBrand"
 */
export const resolveDataPath = (path, data) => {
  if (!path || !data || typeof data !== 'object') return undefined;
  // Normalize bracket notation: items[0].text → items.0.text
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  const keys = normalizedPath.split('.');
  let current = data;
  for (const key of keys) {
    if (current !== null && current !== undefined && typeof current === 'object') {
      // Handle array index access
      if (Array.isArray(current) && /^\d+$/.test(key)) {
        current = current[parseInt(key, 10)];
      } else if (key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    } else {
      return undefined;
    }
  }
  return current;
};

/**
 * Set a value at a dot-separated path in an object (immutable - returns new object)
 * e.g. setDataPath("brand.name", "NewBrand", obj) → new obj with brand.name = "NewBrand"
 */
export const setDataPath = (path, value, data) => {
  if (!path) return data;
  // Normalize bracket notation: items[0].text → items.0.text
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  const keys = normalizedPath.split('.');
  const result = JSON.parse(JSON.stringify(data || {}));
  let current = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];
    if (!(key in current) || typeof current[key] !== 'object') {
      // Create array if next key is numeric, else object
      current[key] = /^\d+$/.test(nextKey) ? [] : {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return result;
};

/**
 * Evaluate a JavaScript-like expression from within {{...}} bindings.
 * Supports: .includes(), .length, ternary (?:), comparisons (===, !==, >, <),
 * logical operators (&&, ||), and negation (!).
 * Matches the customer-side StructureRenderer's evaluateExpression behavior.
 */
export const evaluateExpression = (expression, data, loopContext) => {
  if (!expression || typeof expression !== 'string') return undefined;
  const trimmed = expression.trim();
  const contextData = { ...data, ...(loopContext || {}) };

  // Handle .includes() pattern: "variable.includes('value')"
  const includesMatch = trimmed.match(/^(.+?)\.includes\(['"](.+?)['"]\)$/);
  if (includesMatch) {
    const arrValue = resolveDataPath(includesMatch[1].trim(), contextData);
    if (Array.isArray(arrValue)) return arrValue.includes(includesMatch[2]);
    if (typeof arrValue === 'string') return arrValue.includes(includesMatch[2]);
    return false;
  }

  // Handle .length pattern: "variable.length"
  const lengthMatch = trimmed.match(/^(.+?)\.length$/);
  if (lengthMatch) {
    const val = resolveDataPath(lengthMatch[1].trim(), contextData);
    if (Array.isArray(val)) return val.length;
    if (typeof val === 'string') return val.length;
    return 0;
  }

  // Handle ternary: "condition ? valueIfTrue : valueIfFalse"
  const ternaryMatch = trimmed.match(/^(.+?)\s*\?\s*(.+?)\s*:\s*(.+)$/);
  if (ternaryMatch) {
    const condition = evaluateExpression(ternaryMatch[1].trim(), data, loopContext);
    const truthyVal = ternaryMatch[2].trim();
    const falsyVal = ternaryMatch[3].trim();

    const resolveExprValue = (v) => {
      const strLit = v.match(/^['"](.*)['"]$/);
      if (strLit) return strLit[1];
      if (!isNaN(Number(v)) && v.trim() !== '') return Number(v);
      if (v === 'true') return true;
      if (v === 'false') return false;
      return resolveDataPath(v, contextData);
    };

    return condition ? resolveExprValue(truthyVal) : resolveExprValue(falsyVal);
  }

  // Handle comparisons: ===, !==, ==, !=, >=, <=, >, <
  const compMatch = trimmed.match(/^(.+?)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/);
  if (compMatch) {
    const resolveVal = (v) => {
      v = v.trim();
      const strLit = v.match(/^['"](.*)['"]$/);
      if (strLit) return strLit[1];
      if (!isNaN(Number(v)) && v.trim() !== '') return Number(v);
      if (v === 'true') return true;
      if (v === 'false') return false;
      if (v === 'null') return null;
      if (v === 'undefined') return undefined;
      return resolveDataPath(v, contextData);
    };

    const left = resolveVal(compMatch[1]);
    const right = resolveVal(compMatch[3]);
    const op = compMatch[2];

    switch (op) {
      case '===': case '==': return left === right;
      case '!==': case '!=': return left !== right;
      case '>': return left > right;
      case '<': return left < right;
      case '>=': return left >= right;
      case '<=': return left <= right;
      default: return false;
    }
  }

  // Handle logical operators (&&, ||)
  if (trimmed.includes('&&')) {
    return trimmed.split('&&').every(part => Boolean(evaluateExpression(part.trim(), data, loopContext)));
  }
  if (trimmed.includes('||')) {
    return trimmed.split('||').some(part => Boolean(evaluateExpression(part.trim(), data, loopContext)));
  }

  // Handle negation: "!variable"
  if (trimmed.startsWith('!')) {
    return !evaluateExpression(trimmed.slice(1).trim(), data, loopContext);
  }

  // Simple data path resolution
  return resolveDataPath(trimmed, contextData);
};

// Expression operators that trigger evaluateExpression instead of simple path resolution
const EXPRESSION_OPERATORS = ['.includes(', '.length', '?', '===', '!==', '&&', '||', '>', '<'];

/**
 * Resolve all {{...}} bindings in a string.
 * Supports JavaScript-like expressions matching customer-side StructureRenderer.
 * Returns the resolved string with all bindings replaced.
 */
export const resolveBindings = (content, data, loopContext) => {
  if (!content || typeof content !== 'string') return content || '';
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);

  const bindingPattern = /\{\{([^}]+)\}\}/g;
  return content.replace(bindingPattern, (_match, expression) => {
    const trimmed = expression.trim();

    // Check if it's a JavaScript expression (has operators)
    const isExpression = EXPRESSION_OPERATORS.some(op => trimmed.includes(op));
    if (isExpression) {
      const result = evaluateExpression(trimmed, data, loopContext);
      if (result === undefined || result === null) return '';
      return typeof result === 'object' ? '' : String(result);
    }

    // Simple data path - check loop context first
    if (loopContext) {
      const loopVal = resolveDataPath(trimmed, loopContext);
      if (loopVal !== undefined && loopVal !== null) {
        return typeof loopVal === 'object' ? '' : String(loopVal);
      }
    }
    const val = resolveDataPath(trimmed, data);
    if (val === undefined || val === null) return '';
    return typeof val === 'object' ? '' : String(val);
  });
};

/**
 * Extract the binding path from a {{content.xxx}} string
 * Returns the path if it's a single binding, null otherwise
 * e.g. "{{content.hero.title}}" → "content.hero.title"
 * e.g. "Hello {{name}}" → null (mixed content)
 */
export const extractBindingPath = (content) => {
  if (!content || typeof content !== 'string') return null;
  const match = content.match(/^\{\{([^}]+)\}\}$/);
  return match ? match[1].trim() : null;
};

/**
 * Check if a string contains any {{...}} bindings
 */
export const hasBindings = (content) => {
  if (!content || typeof content !== 'string') return false;
  return /\{\{[^}]+\}\}/.test(content);
};

/**
 * Given a binding path like "content.hero.title", update that value
 * in the template JSON's content section
 * Returns a new content object with the updated value
 */
export const updateContentByBinding = (bindingPath, newValue, content) => {
  if (!bindingPath) return content;

  // Remove "content." prefix if present since we're updating the content object directly
  let path = bindingPath;
  if (path.startsWith('content.')) {
    path = path.substring(8); // Remove "content."
  }

  return setDataPath(path, newValue, content);
};

/**
 * Walk a JSON structure tree and collect all unique binding paths
 * Useful for understanding what data a template needs
 */
export const collectBindingPaths = (structure, paths = new Set()) => {
  if (!structure || typeof structure !== 'object') return paths;

  // Check content field
  if (typeof structure.content === 'string' && hasBindings(structure.content)) {
    const bindingPath = extractBindingPath(structure.content);
    if (bindingPath) paths.add(bindingPath);
  }

  // Check attributes
  if (structure.attributes) {
    Object.values(structure.attributes).forEach(val => {
      if (typeof val === 'string' && hasBindings(val)) {
        const bindingPath = extractBindingPath(val);
        if (bindingPath) paths.add(bindingPath);
      }
    });
  }

  // Check className
  if (typeof structure.className === 'string' && hasBindings(structure.className)) {
    const bindingPath = extractBindingPath(structure.className);
    if (bindingPath) paths.add(bindingPath);
  }

  // Recurse into children
  if (Array.isArray(structure.children)) {
    structure.children.forEach(child => collectBindingPaths(child, paths));
  }

  // Check loop template
  if (structure.loop && structure.loop.data) {
    paths.add(structure.loop.data);
  }

  // Check condition
  if (structure.condition && structure.condition.condition) {
    if (typeof structure.condition.condition === 'string' && !structure.condition.condition.includes(' ')) {
      paths.add(structure.condition.condition);
    }
  }

  return paths;
};

/**
 * Find a node in the JSON structure by its data-path
 * data-path is like "blocks.0.structure.children.2.children.0"
 */
export const findNodeByPath = (json, path) => {
  if (!path || !json) return null;
  const parts = path.split('.');
  let current = json;
  for (const part of parts) {
    if (current === null || current === undefined) return null;
    if (Array.isArray(current)) {
      const idx = parseInt(part, 10);
      if (isNaN(idx) || idx < 0 || idx >= current.length) return null;
      current = current[idx];
    } else if (typeof current === 'object') {
      current = current[part];
    } else {
      return null;
    }
  }
  return current;
};

/**
 * Update a node in the JSON structure by its data-path (immutable)
 * Returns a new JSON object with the node updated
 */
export const updateNodeByPath = (json, path, updater) => {
  if (!path || !json) return json;
  const result = JSON.parse(JSON.stringify(json));
  const parts = path.split('.');
  let current = result;

  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (Array.isArray(current)) {
      current = current[parseInt(part, 10)];
    } else {
      current = current[part];
    }
    if (current === null || current === undefined) return result;
  }

  const lastPart = parts[parts.length - 1];
  if (Array.isArray(current)) {
    const idx = parseInt(lastPart, 10);
    current[idx] = typeof updater === 'function' ? updater(current[idx]) : updater;
  } else {
    current[lastPart] = typeof updater === 'function' ? updater(current[lastPart]) : updater;
  }

  return result;
};

/**
 * Determine if a structure node represents editable text
 * (has content with bindings, or is a text element like h1, h2, p, span)
 */
export const isEditableText = (structure) => {
  if (!structure) return false;
  const textTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'li', 'label', 'button', 'div'];
  const isTextTag = textTags.includes(structure.type);
  const hasTextContent = typeof structure.content === 'string' && structure.content.trim().length > 0;
  const childrenArr = Array.isArray(structure.children) ? structure.children : [];
  const hasNoChildren = childrenArr.length === 0;
  // Allow editing if children are only icons (common pattern: button with text + icon)
  const hasOnlyIconChildren = childrenArr.length > 0 && childrenArr.every(
    c => c.type === 'icon' || (typeof c.type === 'string' && c.type.endsWith('Icon'))
  );
  return isTextTag && hasTextContent && (hasNoChildren || hasOnlyIconChildren);
};

/**
 * Determine if a structure node represents an editable image.
 * Checks both attributes.src (standard) and direct src property (fallback).
 */
export const isEditableImage = (structure) => {
  if (!structure) return false;
  if (structure.type !== 'img') return false;
  return !!(structure.attributes?.src || structure.src);
};

/**
 * Determine if a structure node has a background image that can be edited.
 * Checks for: style.backgroundImage (CSS string or object) and attributes.style with url().
 */
export const isEditableBackgroundImage = (structure) => {
  if (!structure) return false;
  // Check inline style object
  if (structure.style?.backgroundImage) return true;
  // Check style attribute as string
  const styleAttr = structure.attributes?.style;
  if (typeof styleAttr === 'string' && styleAttr.includes('url(')) return true;
  // Check style attribute as object
  if (styleAttr && typeof styleAttr === 'object' && styleAttr.backgroundImage) return true;
  return false;
};

/**
 * Extract the background image URL from a structure node.
 */
export const extractBackgroundImageUrl = (structure, data, loopContext) => {
  if (!structure) return '';
  // From style object
  const bgImg = structure.style?.backgroundImage;
  if (bgImg) {
    const urlMatch = bgImg.match(/url\(['"]?([^'")\s]+)['"]?\)/);
    return urlMatch ? urlMatch[1] : '';
  }
  // From attributes.style string
  const styleAttr = structure.attributes?.style;
  if (typeof styleAttr === 'string') {
    const urlMatch = styleAttr.match(/url\(['"]?([^'")\s]+)['"]?\)/);
    return urlMatch ? urlMatch[1] : '';
  }
  if (styleAttr && typeof styleAttr === 'object' && styleAttr.backgroundImage) {
    const urlMatch = styleAttr.backgroundImage.match(/url\(['"]?([^'")\s]+)['"]?\)/);
    return urlMatch ? urlMatch[1] : '';
  }
  return '';
};

/**
 * Deep clone a JSON object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Update a node's inline style property at a given JSON path (immutable)
 * Returns a new JSON object with the style updated
 * If value is empty/undefined, removes the property from style
 */
export const updateNodeStyle = (json, nodePath, styleProperty, value) => {
  if (!json || !nodePath || !styleProperty) return json;
  const result = JSON.parse(JSON.stringify(json));
  const parts = nodePath.split('.');
  let current = result;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (current === null || current === undefined) return result;
    if (Array.isArray(current)) {
      const idx = parseInt(part, 10);
      if (isNaN(idx) || idx < 0 || idx >= current.length) return result;
      current = current[idx];
    } else if (typeof current === 'object') {
      current = current[part];
    } else {
      return result;
    }
  }
  if (!current || typeof current !== 'object') return result;
  if (!current.style) current.style = {};
  if (value === '' || value === undefined || value === null) {
    delete current.style[styleProperty];
    // Clean up empty style object
    if (Object.keys(current.style).length === 0) delete current.style;
  } else {
    current.style[styleProperty] = value;
  }
  return result;
};
